import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Flex, Heading, Text, Button, Badge, Spinner, useToast, useDisclosure, SimpleGrid, Input, Select,
  Textarea, Stack, HStack, VStack, Divider, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Link as ChakraLink, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel, Tooltip, Switch, FormControl, FormLabel, Radio, RadioGroup,
  Checkbox, Alert, AlertIcon, Center, Tag, Wrap, WrapItem, ButtonGroup, Code,
} from "@chakra-ui/react";
import {
  FaSyncAlt, FaEnvelope, FaPaperPlane, FaRobot, FaUser, FaFilePdf, FaPaperclip, FaCheck, FaTimes,
  FaUndo, FaRedo, FaPlug, FaInbox, FaExternalLinkAlt, FaBan,
} from "react-icons/fa";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  getCotacoesEmail, getCotacaoEmail, getCotacoesEmailDashboard, getCotacoesEmailConfig,
  atualizarCotacoesEmailConfig, testarConexaoCotacoesEmail, pollAgoraCotacoesEmail,
  getCotacoesEmailIgnorados, promoverCotacaoEmailIgnorado, acaoCotacaoEmail,
  enviarRascunhoCotacaoEmail, baixarAnexoCotacaoEmail,
} from "../services/api";
import { formatBRL, formatDateTime } from "../utils/formatters";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const ACCENT = "#F68633";
const SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

const STATUS_META = {
  nova: { label: "Nova", color: "yellow", dica: "E-mail lido; o bot ainda vai processar (ou o bot está desligado)" },
  processando: { label: "Processando", color: "blue", dica: "O bot está pesquisando e escrevendo a resposta" },
  aguardando_cliente: { label: "Aguardando cliente", color: "cyan", dica: "Respondemos com pergunta/informação; a vez é do cliente" },
  cotada: { label: "Cotada", color: "green", dica: "Orçamento em PDF enviado" },
  sem_estoque: { label: "Sem estoque", color: "orange", dica: "Respondemos que não temos os itens" },
  encerrada: { label: "Encerrada", color: "gray", dica: "Conversa terminou sem orçamento" },
  revisar: { label: "Revisar", color: "red", dica: "O bot pediu ajuda de uma pessoa" },
  humano: { label: "Com atendente", color: "purple", dica: "Um atendente assumiu; o bot não responde" },
  convertida: { label: "Convertida", color: "green", dica: "Virou venda" },
  perdida: { label: "Perdida", color: "gray", dica: "Marcada como perdida" },
  ignorada: { label: "Ignorada", color: "gray", dica: "Descartada" },
};
const statusBadge = (s) => {
  const m = STATUS_META[s] || { label: s, color: "gray" };
  return <Tooltip label={m.dica}><Badge colorScheme={m.color} variant={s === "convertida" ? "solid" : "subtle"}>{m.label}</Badge></Tooltip>;
};

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`);
const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtUsd = (v) => "US$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMin = (m) => {
  if (m === null || m === undefined) return "—";
  const n = Number(m);
  if (n < 60) return `${Math.round(n)} min`;
  if (n < 60 * 24) return `${(n / 60).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;
  return `${(n / 1440).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} d`;
};
const fmtDia = (iso) => { const [, m, d] = String(iso).split("-"); return `${d}/${m}`; };
const erroMsg = (e, padrao) => e?.response?.data?.erro || e?.response?.data?.message || padrao;

const Card = ({ titulo, extra, children, ...rest }) => (
  <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} {...rest}>
    {(titulo || extra) && (
      <Flex align="baseline" justify="space-between" mb={3} wrap="wrap" gap={2}>
        <Text fontWeight="semibold">{titulo}</Text>
        {extra}
      </Flex>
    )}
    {children}
  </Box>
);

const StatCard = ({ label, value, hint, onClick, ativo, color }) => (
  <Box bg="white" borderWidth="2px" borderRadius="lg" p={3} cursor={onClick ? "pointer" : "default"}
    borderColor={ativo ? `${color || "orange"}.400` : "gray.100"} onClick={onClick} _hover={onClick ? { borderColor: `${color || "orange"}.300` } : {}}>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
    <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1.2" mt={1}>{value}</Text>
    {hint && <Text fontSize="xs" color="gray.400" mt={1}>{hint}</Text>}
  </Box>
);

/* ========================================================================== */
/* Página                                                                     */
/* ========================================================================== */
export default function CotacoesEmail() {
  const toast = useToast();
  const [cabecalho, setCabecalho] = useState(null); // config resumida (ativo, modo, último poll)
  const [lendo, setLendo] = useState(false);

  const lerCaixaAgora = async () => {
    setLendo(true);
    try {
      const { data } = await pollAgoraCotacoesEmail();
      toast({
        title: "Caixa lida",
        description: `${data.lidos} e-mail(s) novo(s): ${data.novas_cotacoes} cotação(ões), ${data.continuacoes} continuação(ões), ${data.ignorados} ignorado(s).${data.erro_fatal ? " Erro: " + data.erro_fatal : ""}`,
        status: data.erro_fatal ? "error" : "success", duration: 6000, isClosable: true,
      });
    } catch (e) {
      toast({ title: erroMsg(e, "Falha ao ler a caixa"), status: "error", isClosable: true });
    } finally { setLendo(false); }
  };

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        <Box>
          <Heading size="lg" display="flex" alignItems="center" gap={2}><FaEnvelope color={ACCENT} /> Cotações por E-mail</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Bot que lê a caixa vendas@tagaautopartes.com.br, responde os pedidos de cotação e envia o orçamento em PDF.
          </Text>
        </Box>
        <HStack spacing={2} wrap="wrap">
          {cabecalho && (
            <Tooltip label={cabecalho.ultimo_erro ? `Último erro: ${cabecalho.ultimo_erro}` : `Última leitura da caixa: ${formatDateTime(cabecalho.ultimo_poll_em, "nunca")}`}>
              <Tag size="md" colorScheme={cabecalho.ultimo_erro ? "red" : cabecalho.ativo ? "green" : "gray"} variant="subtle">
                <FaRobot style={{ marginRight: 6 }} />
                {cabecalho.ativo ? (cabecalho.modo_envio === "rascunho" ? "Bot ligado (rascunho)" : "Bot ligado") : "Bot desligado"}
              </Tag>
            </Tooltip>
          )}
          <Button size="sm" leftIcon={<FaInbox />} onClick={lerCaixaAgora} isLoading={lendo} variant="outline">Ler caixa agora</Button>
        </HStack>
      </Flex>

      <Tabs colorScheme="orange" variant="enclosed" isLazy>
        <TabList overflowX="auto" overflowY="hidden" sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          <Tab>Cotações</Tab>
          <Tab>Dashboard</Tab>
          <Tab>Ignorados</Tab>
          <Tab>Configurações</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}><AbaCotacoes onCabecalho={setCabecalho} /></TabPanel>
          <TabPanel px={0}><AbaDashboard /></TabPanel>
          <TabPanel px={0}><AbaIgnorados /></TabPanel>
          <TabPanel px={0}><AbaConfiguracoes onSalvo={(c) => setCabecalho((h) => ({ ...(h || {}), ativo: c.ativo, modo_envio: c.modo_envio }))} /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

/* ========================================================================== */
/* Aba: Cotações (lista + drawer)                                              */
/* ========================================================================== */
function AbaCotacoes({ onCabecalho }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({ status: "", busca: "", dias: "", page: 1 });
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [selecionada, setSelecionada] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const carregar = useCallback(async (spin = true) => {
    if (spin) setLoading(true);
    try {
      const params = {};
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await getCotacoesEmail(params);
      setDados(data);
      onCabecalho && onCabecalho(data.config);
    } catch (e) {
      toast({ title: erroMsg(e, "Erro ao carregar as cotações"), status: "error", isClosable: true });
    } finally { setLoading(false); }
  }, [filtros, toast, onCabecalho]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const t = setInterval(() => carregar(false), 30000);
    return () => clearInterval(t);
  }, [carregar]);
  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.busca === buscaDigitada ? f : { ...f, busca: buscaDigitada, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const stats = dados?.stats || {};
  const itens = dados?.itens?.data || [];
  const pag = dados?.itens;

  const abrir = (c) => { setSelecionada(c); onOpen(); };
  const toggleStatus = (s) => setFiltros((f) => ({ ...f, status: f.status === s ? "" : s, page: 1 }));

  const cards = [
    ["nova", "Novas", "yellow"], ["revisar", "Revisar", "red"], ["aguardando_cliente", "Aguardando cliente", "cyan"],
    ["cotada", "Cotadas", "green"], ["humano", "Com atendente", "purple"], ["convertida", "Convertidas", "green"],
  ];

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={4}>
        {cards.map(([k, label, color]) => (
          <StatCard key={k} label={label} value={fmtInt(stats[k])} color={color} ativo={filtros.status === k} onClick={() => toggleStatus(k)} />
        ))}
      </SimpleGrid>

      <Stack direction={{ base: "column", md: "row" }} mb={3} spacing={2} align={{ base: "stretch", md: "center" }}>
        <Select w={{ base: "100%", md: "230px" }} bg="white" value={filtros.status} onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">Todos os status</option>
          <option value="abertas">Em aberto (nova, aguardando, cotada, revisar, atendente)</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </Select>
        <Select w={{ base: "100%", md: "170px" }} bg="white" value={filtros.dias} onChange={(e) => setFiltros((f) => ({ ...f, dias: e.target.value, page: 1 }))}>
          <option value="">Todo o período</option>
          <option value="7">Últimos 7 dias</option>
          <option value="30">Últimos 30 dias</option>
          <option value="90">Últimos 90 dias</option>
        </Select>
        <Input w={{ base: "100%", md: "320px" }} bg="white" placeholder="Buscar empresa, e-mail, assunto, nº do orçamento" value={buscaDigitada} onChange={(e) => setBuscaDigitada(e.target.value)} />
        <Button leftIcon={<FaSyncAlt />} size="md" variant="outline" onClick={() => carregar()} isLoading={loading}>Atualizar</Button>
      </Stack>

      {loading && !dados ? (
        <Center py={16}><Spinner size="xl" color={ACCENT} thickness="4px" /></Center>
      ) : itens.length === 0 ? (
        <Card><Text color="gray.500" textAlign="center" py={6}>Nenhuma cotação encontrada com esses filtros.</Text></Card>
      ) : (
        <Box opacity={loading ? 0.6 : 1} transition="opacity .15s">
          {/* Desktop */}
          <Box display={{ base: "none", lg: "block" }} bg="white" borderWidth="1px" borderRadius="lg" overflow="hidden">
            <TableContainer>
              <Table size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Solicitante</Th><Th>Assunto / resumo</Th><Th>Status</Th><Th isNumeric>Orçamento</Th><Th isNumeric>E-mails</Th><Th>Última atividade</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itens.map((c) => (
                    <Tr key={c.id} cursor="pointer" _hover={{ bg: "orange.50" }} onClick={() => abrir(c)}>
                      <Td maxW="260px">
                        <Text fontWeight="semibold" noOfLines={1}>{c.empresa || c.remetente_nome || c.remetente_email}</Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.remetente_email}</Text>
                      </Td>
                      <Td maxW="420px">
                        <Text noOfLines={1} fontSize="sm">{c.assunto || "(sem assunto)"}</Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.status === "revisar" && c.status_motivo ? c.status_motivo : (c.resumo || "")}</Text>
                      </Td>
                      <Td>{statusBadge(c.status)}</Td>
                      <Td isNumeric>
                        {c.orcamento_codigo ? (
                          <>
                            <Text fontSize="sm" fontWeight="semibold">{formatBRL(c.valor_total)}</Text>
                            <Text fontSize="xs" color="gray.500">{c.orcamento_codigo} · {c.qtd_itens} item(ns)</Text>
                          </>
                        ) : <Text fontSize="xs" color="gray.400">—</Text>}
                        {c.status === "convertida" && <Text fontSize="xs" color="green.600">venda {formatBRL(c.valor_convertido)}</Text>}
                      </Td>
                      <Td isNumeric><Text fontSize="sm">{c.total_recebidos} ↓ / {c.total_enviados} ↑</Text></Td>
                      <Td><Text fontSize="xs">{formatDateTime(c.updated_at)}</Text></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          {/* Mobile */}
          <VStack display={{ base: "flex", lg: "none" }} align="stretch" spacing={2}>
            {itens.map((c) => (
              <Box key={c.id} bg="white" borderWidth="1px" borderRadius="lg" p={3} onClick={() => abrir(c)} cursor="pointer">
                <Flex justify="space-between" align="start" gap={2}>
                  <Box minW={0}>
                    <Text fontWeight="semibold" noOfLines={1}>{c.empresa || c.remetente_nome || c.remetente_email}</Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.assunto}</Text>
                  </Box>
                  {statusBadge(c.status)}
                </Flex>
                <HStack mt={2} fontSize="xs" color="gray.600" spacing={3}>
                  {c.orcamento_codigo && <Text>{formatBRL(c.valor_total)}</Text>}
                  <Text>{c.total_recebidos} ↓ / {c.total_enviados} ↑</Text>
                  <Text>{formatDateTime(c.updated_at)}</Text>
                </HStack>
              </Box>
            ))}
          </VStack>
          {pag && pag.last_page > 1 && (
            <HStack justify="center" mt={4}>
              <Button size="sm" isDisabled={pag.current_page <= 1} onClick={() => setFiltros((f) => ({ ...f, page: pag.current_page - 1 }))}>Anterior</Button>
              <Text fontSize="sm">Página {pag.current_page} de {pag.last_page} · {pag.total} cotações</Text>
              <Button size="sm" isDisabled={pag.current_page >= pag.last_page} onClick={() => setFiltros((f) => ({ ...f, page: pag.current_page + 1 }))}>Próxima</Button>
            </HStack>
          )}
        </Box>
      )}

      <DrawerCotacao id={selecionada?.id} isOpen={isOpen} onClose={onClose} onMudou={() => carregar(false)} />
    </Box>
  );
}

/* ========================================================================== */
/* Drawer: thread da cotação                                                  */
/* ========================================================================== */
function DrawerCotacao({ id, isOpen, onClose, onMudou }) {
  const toast = useToast();
  const [detalhe, setDetalhe] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [agindo, setAgindo] = useState("");
  const [resposta, setResposta] = useState("");
  const [anexarPdf, setAnexarPdf] = useState(true);
  const [motivoPerda, setMotivoPerda] = useState("");
  const [mostrarPerda, setMostrarPerda] = useState(false);
  const [mostrarResposta, setMostrarResposta] = useState(false);

  const carregar = useCallback(async () => {
    if (!id) return;
    setCarregando(true);
    try {
      const { data } = await getCotacaoEmail(id);
      setDetalhe(data);
    } catch (e) {
      toast({ title: erroMsg(e, "Erro ao abrir a cotação"), status: "error", isClosable: true });
    } finally { setCarregando(false); }
  }, [id, toast]);

  useEffect(() => { if (isOpen) { setDetalhe(null); setResposta(""); setMostrarPerda(false); setMostrarResposta(false); carregar(); } }, [isOpen, carregar]);
  useEffect(() => {
    if (!isOpen || !detalhe) return undefined;
    const st = detalhe.cotacao?.status;
    if (st !== "processando" && st !== "nova") return undefined;
    const t = setInterval(carregar, 8000);
    return () => clearInterval(t);
  }, [isOpen, detalhe, carregar]);

  const c = detalhe?.cotacao;
  const mensagens = detalhe?.mensagens || [];

  const acao = async (nome, dados = {}, msgOk = "Feito") => {
    setAgindo(nome);
    try {
      await acaoCotacaoEmail(id, nome, dados);
      toast({ title: msgOk, status: "success", duration: 2500, position: "top-right" });
      await carregar();
      onMudou && onMudou();
    } catch (e) {
      toast({ title: erroMsg(e, "Não foi possível executar a ação"), status: "error", isClosable: true });
    } finally { setAgindo(""); }
  };

  const enviarResposta = async () => {
    if (resposta.trim().length < 5) return;
    setAgindo("responder");
    try {
      await acaoCotacaoEmail(id, "responder", { corpo: resposta, anexar_pdf: anexarPdf && !!c?.orcamento_codigo });
      toast({ title: "E-mail enviado", status: "success", duration: 2500, position: "top-right" });
      setResposta(""); setMostrarResposta(false);
      await carregar(); onMudou && onMudou();
    } catch (e) {
      toast({ title: erroMsg(e, "Falha ao enviar"), status: "error", isClosable: true });
    } finally { setAgindo(""); }
  };

  const enviarRascunho = async (msgId) => {
    setAgindo("rascunho" + msgId);
    try {
      await enviarRascunhoCotacaoEmail(id, msgId);
      toast({ title: "E-mail enviado ao cliente", status: "success", duration: 2500, position: "top-right" });
      await carregar(); onMudou && onMudou();
    } catch (e) {
      toast({ title: erroMsg(e, "Falha ao enviar"), status: "error", isClosable: true });
    } finally { setAgindo(""); }
  };

  const baixar = async (msgId, a) => {
    try { await baixarAnexoCotacaoEmail(id, msgId, a.idx, a.nome); } catch { toast({ title: "Não foi possível baixar o anexo", status: "error" }); }
  };

  const st = c?.status;
  const podeAssumir = c && st !== "humano";
  const emAberto = c && !["convertida", "perdida", "ignorada"].includes(st);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }} placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={2}>
          {c ? (
            <Box pr={8}>
              <HStack spacing={2} wrap="wrap">
                <Text>{c.empresa || c.remetente_nome || c.remetente_email}</Text>
                {statusBadge(c.status)}
                {c.humano && <Tag size="sm" colorScheme="purple"><FaUser style={{ marginRight: 4 }} />{c.humano.name}</Tag>}
              </HStack>
              <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>{c.remetente_nome ? `${c.remetente_nome} · ` : ""}{c.remetente_email}</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.500" noOfLines={1}>{c.assunto}</Text>
            </Box>
          ) : `Cotação #${id || ""}`}
        </DrawerHeader>
        <DrawerBody pb={8}>
          {!detalhe ? (
            <Center py={10}><Spinner color={ACCENT} /></Center>
          ) : (
            <VStack align="stretch" spacing={4} opacity={carregando ? 0.6 : 1} transition="opacity .15s">
              {c.status_motivo && (st === "revisar" || st === "nova") && (
                <Alert status={st === "revisar" ? "warning" : "info"} borderRadius="md" fontSize="sm"><AlertIcon />{c.status_motivo}</Alert>
              )}
              {c.ultimo_erro && st === "revisar" && (
                <Alert status="error" borderRadius="md" fontSize="xs"><AlertIcon />{c.ultimo_erro}</Alert>
              )}

              {/* Ações */}
              <Wrap spacing={2}>
                {emAberto && podeAssumir && <WrapItem><Button size="sm" leftIcon={<FaUser />} colorScheme="purple" variant="outline" isLoading={agindo === "assumir"} onClick={() => acao("assumir", {}, "Você assumiu esta cotação; o bot não responde mais nela")}>Assumir</Button></WrapItem>}
                {st === "humano" && <WrapItem><Button size="sm" leftIcon={<FaRobot />} variant="outline" isLoading={agindo === "devolver-bot"} onClick={() => acao("devolver-bot", {}, "Devolvida ao bot")}>Devolver ao bot</Button></WrapItem>}
                {emAberto && <WrapItem><Button size="sm" leftIcon={<FaRedo />} variant="outline" isLoading={agindo === "reprocessar"} onClick={() => acao("reprocessar", {}, "Bot reprocessando o último e-mail")}>Reprocessar (bot)</Button></WrapItem>}
                {emAberto && <WrapItem><Button size="sm" leftIcon={<FaPaperPlane />} colorScheme="orange" variant={mostrarResposta ? "solid" : "outline"} onClick={() => setMostrarResposta((v) => !v)}>Responder manualmente</Button></WrapItem>}
                {emAberto && <WrapItem><Button size="sm" leftIcon={<FaCheck />} colorScheme="green" variant="outline" isLoading={agindo === "convertida"} onClick={() => acao("convertida", {}, "Marcada como convertida")}>Marcar convertida</Button></WrapItem>}
                {emAberto && <WrapItem><Button size="sm" leftIcon={<FaTimes />} variant="outline" onClick={() => setMostrarPerda((v) => !v)}>Marcar perdida</Button></WrapItem>}
                {emAberto && <WrapItem><Button size="sm" leftIcon={<FaBan />} variant="ghost" colorScheme="gray" isLoading={agindo === "ignorar"} onClick={() => acao("ignorar", {}, "Cotação ignorada")}>Ignorar</Button></WrapItem>}
                {!emAberto && <WrapItem><Button size="sm" leftIcon={<FaUndo />} variant="outline" isLoading={agindo === "reabrir"} onClick={() => acao("reabrir", {}, "Cotação reaberta")}>Reabrir</Button></WrapItem>}
              </Wrap>

              {mostrarPerda && (
                <Card bg="gray.50">
                  <Text fontSize="sm" mb={2}>Motivo da perda (opcional)</Text>
                  <Input size="sm" bg="white" value={motivoPerda} onChange={(e) => setMotivoPerda(e.target.value)} placeholder="Ex.: fechou com concorrente, preço, prazo" />
                  <HStack mt={2}><Button size="sm" colorScheme="red" isLoading={agindo === "perdida"} onClick={() => { acao("perdida", { motivo: motivoPerda }, "Marcada como perdida"); setMostrarPerda(false); }}>Confirmar</Button><Button size="sm" variant="ghost" onClick={() => setMostrarPerda(false)}>Cancelar</Button></HStack>
                </Card>
              )}

              {mostrarResposta && (
                <Card bg="orange.50" borderColor="orange.200">
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>Responder como atendente (sai de vendas@, na mesma thread)</Text>
                  <Textarea bg="white" rows={7} value={resposta} onChange={(e) => setResposta(e.target.value)} placeholder={"Olá,\n\n..."} />
                  <HStack mt={2} justify="space-between" wrap="wrap">
                    {c.orcamento_codigo ? <Checkbox isChecked={anexarPdf} onChange={(e) => setAnexarPdf(e.target.checked)} size="sm">Anexar o PDF {c.orcamento_codigo}</Checkbox> : <Box />}
                    <Button size="sm" colorScheme="orange" leftIcon={<FaPaperPlane />} isLoading={agindo === "responder"} onClick={enviarResposta} isDisabled={resposta.trim().length < 5}>Enviar e-mail</Button>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>Ao responder manualmente, você assume a cotação e o bot para de responder nela.</Text>
                </Card>
              )}

              {/* Resumo / orçamento / cliente */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Card titulo="Resumo (bot)">
                  <Text fontSize="sm" whiteSpace="pre-wrap">{c.resumo || <Text as="span" color="gray.400">Ainda sem resumo.</Text>}</Text>
                  <HStack mt={3} fontSize="xs" color="gray.500" spacing={4} wrap="wrap">
                    <Text>1º e-mail: {formatDateTime(c.primeiro_email_em)}</Text>
                    <Text>1ª resposta: {formatDateTime(c.primeira_resposta_em, "—")}</Text>
                    <Text>Rodadas IA: {c.rodadas_ia}</Text>
                  </HStack>
                </Card>
                <Card titulo="Orçamento">
                  {c.orcamento_codigo ? (
                    <VStack align="stretch" spacing={1} fontSize="sm">
                      <HStack><Text fontWeight="semibold">{c.orcamento_codigo}</Text><Text color="gray.500">· {c.qtd_itens} item(ns)</Text></HStack>
                      <Text fontSize="lg" fontWeight="bold" color="green.700">{formatBRL(c.valor_total)}</Text>
                      <Text fontSize="xs" color="gray.500">Emitido em {formatDateTime(c.cotada_em)}</Text>
                      {c.pdf_url && <ChakraLink href={c.pdf_url} isExternal color="orange.600" fontSize="sm"><FaFilePdf style={{ display: "inline", marginRight: 4 }} />Abrir PDF <FaExternalLinkAlt style={{ display: "inline", marginLeft: 4 }} size={10} /></ChakraLink>}
                      {st === "convertida" && <Text color="green.600">Convertida em {formatDateTime(c.convertida_em)} — {formatBRL(c.valor_convertido)}{c.venda_id ? ` (venda #${c.venda_id})` : ""}{c.conversao_origem ? ` · ${c.conversao_origem}` : ""}</Text>}
                    </VStack>
                  ) : <Text fontSize="sm" color="gray.400">Nenhum orçamento gerado ainda.</Text>}
                  {c.dados_cliente && Object.keys(c.dados_cliente).length > 0 && (
                    <Box mt={3} fontSize="xs" color="gray.600">
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Dados do cliente (extraídos)</Text>
                      <Wrap spacing={2}>{Object.entries(c.dados_cliente).map(([k, v]) => v ? <WrapItem key={k}><Tag size="sm" variant="subtle">{k}: {String(v)}</Tag></WrapItem> : null)}</Wrap>
                    </Box>
                  )}
                </Card>
              </SimpleGrid>

              {Array.isArray(c.itens) && c.itens.length > 0 && (
                <Card titulo="Itens solicitados">
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Item pedido</Th><Th>Cód. informado</Th><Th isNumeric>Qtd</Th><Th>Situação</Th><Th>Cód. TAGA</Th><Th isNumeric>Unit.</Th></Tr></Thead>
                      <Tbody>
                        {c.itens.map((it, i) => (
                          <Tr key={i}>
                            <Td maxW="260px"><Text noOfLines={2} fontSize="xs">{it.descricao}</Text>{it.observacao && <Text fontSize="xs" color="gray.500">{it.observacao}</Text>}</Td>
                            <Td fontSize="xs">{it.codigo_informado || "—"}</Td>
                            <Td isNumeric fontSize="xs">{it.quantidade ?? "—"}</Td>
                            <Td><Badge colorScheme={it.situacao === "encontrado" ? "green" : it.situacao === "parcial" ? "orange" : it.situacao === "duvida" ? "yellow" : "red"}>{it.situacao}</Badge></Td>
                            <Td fontSize="xs">{it.codigo_global || "—"}</Td>
                            <Td isNumeric fontSize="xs">{it.preco_unit ? formatBRL(it.preco_unit) : "—"}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Card>
              )}

              {/* Thread */}
              <Box>
                <Text fontWeight="semibold" mb={2}>Conversa ({mensagens.length})</Text>
                <VStack align="stretch" spacing={3}>
                  {mensagens.map((m) => {
                    const enviada = m.direcao === "enviada";
                    const rascunho = m.classificacao === "rascunho";
                    const erroEnvio = m.classificacao === "erro_envio";
                    return (
                      <Box key={m.id} alignSelf={enviada ? "flex-end" : "flex-start"} maxW={{ base: "100%", md: "92%" }} w="100%"
                        bg={erroEnvio ? "red.50" : rascunho ? "yellow.50" : enviada ? "orange.50" : "white"}
                        borderWidth="1px" borderColor={erroEnvio ? "red.200" : rascunho ? "yellow.300" : enviada ? "orange.200" : "gray.200"} borderRadius="lg" p={3}>
                        <Flex justify="space-between" align="start" gap={2} wrap="wrap">
                          <HStack spacing={2} fontSize="xs" color="gray.600">
                            {enviada ? (m.classificacao === "humano" ? <FaUser /> : <FaRobot />) : <FaEnvelope />}
                            <Text fontWeight="semibold">{enviada ? (m.classificacao === "humano" ? "Atendente" : "Bot TAGA") : (m.de_nome || m.de_email)}</Text>
                            <Text>{formatDateTime(m.data_email)}</Text>
                            {rascunho && <Badge colorScheme="yellow">Rascunho — não enviado</Badge>}
                            {erroEnvio && <Badge colorScheme="red">Falha no envio</Badge>}
                            {m.tipo_resposta && !rascunho && !erroEnvio && <Badge variant="outline" colorScheme="orange">{m.tipo_resposta}</Badge>}
                            {!enviada && !m.processada_em && <Badge colorScheme="yellow">pendente</Badge>}
                          </HStack>
                          {(rascunho || erroEnvio) && (
                            <Button size="xs" colorScheme="orange" leftIcon={<FaPaperPlane />} isLoading={agindo === "rascunho" + m.id} onClick={() => enviarRascunho(m.id)}>{erroEnvio ? "Reenviar" : "Aprovar e enviar"}</Button>
                          )}
                        </Flex>
                        <Text fontSize="xs" color="gray.500" mt={1}>Assunto: {m.assunto}{enviada && m.para ? ` · para ${m.para}` : ""}</Text>
                        {erroEnvio && m.motivo && <Text fontSize="xs" color="red.600" mt={1}>{m.motivo}</Text>}
                        <Text fontSize="sm" whiteSpace="pre-wrap" mt={2} wordBreak="break-word">{m.corpo_texto || <Text as="span" color="gray.400">(sem texto)</Text>}</Text>
                        {m.anexos && m.anexos.length > 0 && (
                          <Wrap mt={2} spacing={2}>
                            {m.anexos.map((a) => (
                              <WrapItem key={a.idx}>
                                <Button size="xs" variant="outline" leftIcon={a.tipo === "pdf" ? <FaFilePdf /> : <FaPaperclip />} onClick={() => baixar(m.id, a)}>
                                  {a.nome}{a.tamanho ? ` (${Math.round(a.tamanho / 1024)} KB)` : ""}
                                </Button>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/* ========================================================================== */
/* Aba: Dashboard                                                             */
/* ========================================================================== */
function AbaDashboard() {
  const toast = useToast();
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await getCotacoesEmailDashboard({ dias: d });
      setDados(data);
    } catch (e) {
      toast({ title: erroMsg(e, "Erro ao carregar o dashboard"), status: "error", isClosable: true });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { carregar(dias); }, [carregar, dias]);

  const k = dados?.kpis || {};
  const ka = dados?.kpis_anterior || {};
  const serie = useMemo(() => dados?.serie || [], [dados]);

  const chartData = useMemo(() => ({
    labels: serie.map((l) => fmtDia(l.dia)),
    datasets: [
      { label: "Recebidas", data: serie.map((l) => l.recebidas), backgroundColor: SERIES[0] },
      { label: "Cotadas", data: serie.map((l) => l.cotadas), backgroundColor: SERIES[3] },
      { label: "Convertidas", data: serie.map((l) => l.convertidas), backgroundColor: SERIES[2] },
    ],
  }), [serie]);
  const chartOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { position: "bottom" } },
    scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
  }), []);

  const funil = [
    ["Recebidas", k.recebidas, SERIES[0]],
    ["Respondidas", k.respondidas, SERIES[1]],
    ["Cotadas (PDF)", k.cotadas, SERIES[3]],
    ["Convertidas", k.convertidas, SERIES[2]],
  ];
  const base = Math.max(1, k.recebidas || 0);
  const delta = (a, b) => (b ? `${a >= b ? "+" : ""}${fmtInt(a - b)} vs. período anterior` : undefined);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
        <ButtonGroup size="sm" isAttached variant="outline">
          {[7, 15, 30, 90].map((d) => <Button key={d} onClick={() => setDias(d)} bg={dias === d ? "orange.100" : "white"}>{d} dias</Button>)}
        </ButtonGroup>
        <Button size="sm" variant="ghost" leftIcon={<FaSyncAlt />} isLoading={loading} onClick={() => carregar(dias)}>Atualizar</Button>
      </Flex>
      {loading && !dados ? <Center py={16}><Spinner size="xl" color={ACCENT} thickness="4px" /></Center> : dados && (
        <Box opacity={loading ? 0.6 : 1}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
            <StatCard label="Cotações recebidas" value={fmtInt(k.recebidas)} hint={delta(k.recebidas, ka.recebidas)} />
            <StatCard label="Cotadas (PDF enviado)" value={fmtInt(k.cotadas)} hint={`${fmtPct(k.taxa_cotacao)} das recebidas`} />
            <StatCard label="Convertidas em venda" value={fmtInt(k.convertidas)} hint={`${fmtPct(k.taxa_conversao)} das cotadas · ${fmtPct(k.taxa_conversao_geral)} do total`} />
            <StatCard label="Taxa de resposta" value={fmtPct(k.taxa_resposta)} hint={`1ª resposta em média ${fmtMin(k.minutos_primeira_resposta)}`} />
            <StatCard label="Valor cotado" value={formatBRL(k.valor_cotado)} hint={delta(Math.round(k.valor_cotado || 0), Math.round(ka.valor_cotado || 0)) ? `${formatBRL(ka.valor_cotado)} no período anterior` : undefined} />
            <StatCard label="Valor convertido" value={formatBRL(k.valor_convertido)} hint={`ticket médio ${formatBRL(k.ticket_medio_convertido)}`} />
            <StatCard label="Precisam de atenção" value={fmtInt((k.revisar || 0) + (k.humano || 0))} hint={`${fmtInt(k.revisar)} revisar · ${fmtInt(k.humano)} com atendente`} />
            <StatCard label="Custo de IA" value={fmtUsd(dados.custo_ia?.total_usd)} hint={`${fmtInt(dados.custo_ia?.chamadas)} chamadas · ${fmtInt(dados.emails?.lidos)} e-mails lidos, ${fmtInt(dados.emails?.ignorados)} ignorados`} />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3} mb={4}>
            <Card titulo="Funil">
              <VStack align="stretch" spacing={3}>
                {funil.map(([label, v, cor]) => (
                  <Box key={label}>
                    <Flex justify="space-between" fontSize="sm"><Text>{label}</Text><Text fontWeight="semibold">{fmtInt(v)} <Text as="span" color="gray.400" fontWeight="normal">({fmtPct(100 * (v || 0) / base)})</Text></Text></Flex>
                    <Box h="10px" bg="gray.100" borderRadius="full" overflow="hidden" mt={1}><Box h="100%" w={`${Math.min(100, 100 * (v || 0) / base)}%`} bg={cor} borderRadius="full" /></Box>
                  </Box>
                ))}
                <Divider />
                <SimpleGrid columns={2} spacing={2} fontSize="xs" color="gray.600">
                  <Text>Aguardando cliente: <b>{fmtInt(k.aguardando)}</b></Text>
                  <Text>Sem estoque: <b>{fmtInt(k.sem_estoque)}</b></Text>
                  <Text>Perdidas: <b>{fmtInt(k.perdidas)}</b></Text>
                  <Text>Descartadas: <b>{fmtInt(k.descartadas)}</b></Text>
                </SimpleGrid>
              </VStack>
            </Card>
            <Card titulo="Por dia" gridColumn={{ lg: "span 2" }}>
              {serie.length === 0 ? <Text color="gray.500" py={8} textAlign="center">Sem dados no período.</Text> : (
                <Box h={{ base: "260px", md: "300px" }}><Bar data={chartData} options={chartOptions} /></Box>
              )}
            </Card>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            <Card titulo="Quem mais pede cotação">
              {(dados.top_empresas || []).length === 0 ? <Text fontSize="sm" color="gray.500">Nada ainda.</Text> : (
                <TableContainer>
                  <Table size="sm">
                    <Thead><Tr><Th>Solicitante</Th><Th isNumeric>Cotações</Th><Th isNumeric>Cotadas</Th><Th isNumeric>Conv.</Th><Th isNumeric>Valor cotado</Th></Tr></Thead>
                    <Tbody>
                      {dados.top_empresas.map((e, i) => (
                        <Tr key={i}><Td maxW="220px"><Text noOfLines={1} fontSize="sm">{e.nome}</Text><Text fontSize="xs" color="gray.500" noOfLines={1}>{e.remetente_email}</Text></Td><Td isNumeric>{e.cotacoes}</Td><Td isNumeric>{e.cotadas}</Td><Td isNumeric>{e.convertidas}</Td><Td isNumeric>{formatBRL(e.valor_cotado)}</Td></Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Card>
            <Card titulo="Por status (no período)">
              <TableContainer>
                <Table size="sm">
                  <Thead><Tr><Th>Status</Th><Th isNumeric>Qtd</Th><Th isNumeric>Valor cotado</Th></Tr></Thead>
                  <Tbody>{(dados.por_status || []).map((s) => <Tr key={s.status}><Td>{statusBadge(s.status)}</Td><Td isNumeric>{s.total}</Td><Td isNumeric>{formatBRL(s.valor)}</Td></Tr>)}</Tbody>
                </Table>
              </TableContainer>
              {(dados.revisar || []).length > 0 && (
                <Box mt={4}>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>O bot pediu ajuda em:</Text>
                  <VStack align="stretch" spacing={1}>
                    {dados.revisar.map((r) => <Text key={r.id} fontSize="xs" color="gray.700">#{r.id} {r.empresa}: <Text as="span" color="gray.500">{r.motivo}</Text></Text>)}
                  </VStack>
                </Box>
              )}
            </Card>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
}

/* ========================================================================== */
/* Aba: Ignorados                                                             */
/* ========================================================================== */
function AbaIgnorados() {
  const toast = useToast();
  const [dias, setDias] = useState(7);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promovendo, setPromovendo] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try { const { data } = await getCotacoesEmailIgnorados({ dias }); setItens(data.itens || []); }
    catch (e) { toast({ title: erroMsg(e, "Erro ao carregar"), status: "error" }); }
    finally { setLoading(false); }
  }, [dias, toast]);
  useEffect(() => { carregar(); }, [carregar]);

  const promover = async (m) => {
    setPromovendo(m.id);
    try {
      const { data } = await promoverCotacaoEmailIgnorado(m.id);
      toast({ title: `Virou a cotação #${data.cotacao_id}; o bot vai processar`, status: "success", duration: 3500, position: "top-right" });
      carregar();
    } catch (e) { toast({ title: erroMsg(e, "Não foi possível promover"), status: "error" }); }
    finally { setPromovendo(null); }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
        <Text fontSize="sm" color="gray.600">E-mails lidos que <b>não</b> viraram cotação (automáticos, newsletters, fornecedores…). Se algum for cotação de verdade, clique em "É cotação".</Text>
        <HStack>
          <Select size="sm" bg="white" w="140px" value={dias} onChange={(e) => setDias(Number(e.target.value))}><option value={7}>7 dias</option><option value={30}>30 dias</option><option value={90}>90 dias</option></Select>
          <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} isLoading={loading} onClick={carregar}>Atualizar</Button>
        </HStack>
      </Flex>
      {loading && itens.length === 0 ? <Center py={10}><Spinner color={ACCENT} /></Center> : itens.length === 0 ? <Card><Text color="gray.500" textAlign="center" py={4}>Nada ignorado no período.</Text></Card> : (
        <VStack align="stretch" spacing={2}>
          {itens.map((m) => (
            <Box key={m.id} bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Flex justify="space-between" gap={3} wrap="wrap" align="start">
                <Box minW={0} flex="1">
                  <HStack spacing={2} wrap="wrap">
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{m.de_nome || m.de_email}</Text>
                    <Text fontSize="xs" color="gray.500">{m.de_email}</Text>
                    <Text fontSize="xs" color="gray.400">{formatDateTime(m.data_email)}</Text>
                    <Badge colorScheme={m.classificacao === "automatico" ? "gray" : "orange"}>{m.classificacao}</Badge>
                  </HStack>
                  <Text fontSize="sm" noOfLines={1} mt={1}>{m.assunto || "(sem assunto)"}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={2}>{m.previa}</Text>
                  <Text fontSize="xs" color="orange.700" mt={1}>Motivo: {m.motivo}{m.anexos?.length ? ` · anexos: ${m.anexos.join(", ")}` : ""}</Text>
                </Box>
                <Button size="xs" colorScheme="orange" variant="outline" isLoading={promovendo === m.id} onClick={() => promover(m)}>É cotação → processar</Button>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
}

/* ========================================================================== */
/* Aba: Configurações                                                         */
/* ========================================================================== */
function AbaConfiguracoes({ onSalvo }) {
  const toast = useToast();
  const [cfg, setCfg] = useState(null);
  const [padrao, setPadrao] = useState("");
  const [conta, setConta] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getCotacoesEmailConfig();
        setCfg({ ...data.config, system_prompt: data.config.system_prompt_em_uso || "" });
        setPadrao(data.system_prompt_padrao || "");
        setConta(data.conta);
      } catch (e) { toast({ title: erroMsg(e, "Erro ao carregar a configuração"), status: "error" }); }
    })();
  }, [toast]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const payload = {
        ativo: !!cfg.ativo, modo_envio: cfg.modo_envio || "automatico",
        system_prompt: cfg.system_prompt, remetentes_ignorados: cfg.remetentes_ignorados || "", assinatura: cfg.assinatura || "",
      };
      const { data } = await atualizarCotacoesEmailConfig(payload);
      toast({ title: "Configuração salva", status: "success", duration: 2500, position: "top-right" });
      onSalvo && onSalvo(data);
    } catch (e) { toast({ title: erroMsg(e, "Erro ao salvar"), status: "error" }); }
    finally { setSalvando(false); }
  };

  const testar = async () => {
    setTestando(true); setTeste(null);
    try { const { data } = await testarConexaoCotacoesEmail(); setTeste(data); }
    catch (e) { toast({ title: erroMsg(e, "Falha no teste"), status: "error" }); }
    finally { setTestando(false); }
  };

  if (!cfg) return <Center py={10}><Spinner color={ACCENT} /></Center>;

  return (
    <VStack align="stretch" spacing={4} maxW="1000px">
      <Card titulo="Bot">
        <Stack spacing={4}>
          <FormControl display="flex" alignItems="center">
            <Switch id="ativo" colorScheme="orange" isChecked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })} />
            <FormLabel htmlFor="ativo" mb={0} ml={3}>{cfg.ativo ? "Bot ligado — responde as cotações sozinho" : "Bot desligado — a caixa continua sendo lida e classificada, mas ninguém responde"}</FormLabel>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Modo de envio</FormLabel>
            <RadioGroup value={cfg.modo_envio || "automatico"} onChange={(v) => setCfg({ ...cfg, modo_envio: v })}>
              <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                <Radio value="automatico" colorScheme="orange">Automático — o bot envia o e-mail e o PDF direto ao cliente</Radio>
                <Radio value="rascunho" colorScheme="orange">Rascunho — o bot prepara; alguém aprova e envia na tela</Radio>
              </Stack>
            </RadioGroup>
            <Text fontSize="xs" color="gray.500" mt={1}>Dica: comece em rascunho por alguns dias e passe para automático quando as respostas estiverem no ponto.</Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Remetentes ignorados (um por linha: e-mail completo ou @dominio.com.br)</FormLabel>
            <Textarea rows={4} fontFamily="mono" fontSize="xs" value={cfg.remetentes_ignorados || ""} onChange={(e) => setCfg({ ...cfg, remetentes_ignorados: e.target.value })} placeholder={"@nvoip.com.br\nnewsletter@exemplo.com"} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Rodapé dos e-mails (vazio = padrão: TAGA Autopartes, e-mail, WhatsApp e site). O fecho "Atenciosamente, Equipe de Vendas" já vem no texto da resposta.</FormLabel>
            <Textarea rows={4} fontSize="sm" value={cfg.assinatura || ""} onChange={(e) => setCfg({ ...cfg, assinatura: e.target.value })} />
          </FormControl>
        </Stack>
      </Card>

      <Card titulo="Prompt do bot (instruções)" extra={<Button size="xs" variant="ghost" onClick={() => setCfg({ ...cfg, system_prompt: padrao })}>Restaurar padrão</Button>}>
        <Textarea rows={22} fontFamily="mono" fontSize="xs" value={cfg.system_prompt || ""} onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })} />
        <Text fontSize="xs" color="gray.500" mt={1}>Vale a partir da próxima cotação processada. Salvar igual ao padrão volta a usar o texto do sistema (atualizado com o código).</Text>
      </Card>

      <Card titulo="Conta de e-mail" extra={<Button size="xs" leftIcon={<FaPlug />} variant="outline" isLoading={testando} onClick={testar}>Testar conexão</Button>}>
        {conta && <Text fontSize="sm" color="gray.600">Caixa: <Code>{conta.email}</Code> · IMAP <Code>{conta.imap}</Code> · SMTP <Code>{conta.smtp}</Code> (credenciais no servidor)</Text>}
        {teste && (
          <HStack mt={2} spacing={3} wrap="wrap">
            <Badge colorScheme={teste.imap?.ok ? "green" : "red"}>IMAP {teste.imap?.ok ? `OK (${fmtInt(teste.imap.mensagens_inbox)} na caixa)` : `falhou: ${teste.imap?.erro}`}</Badge>
            <Badge colorScheme={teste.smtp?.ok ? "green" : "red"}>SMTP {teste.smtp?.ok ? "OK" : `falhou: ${teste.smtp?.erro}`}</Badge>
          </HStack>
        )}
      </Card>

      <HStack justify="flex-end">
        <Button colorScheme="orange" isLoading={salvando} onClick={salvar}>Salvar configurações</Button>
      </HStack>
    </VStack>
  );
}
